import React, { memo, useEffect, useMemo, useState } from "react";
import VirtualList from "../../components/ui/VirtualList";
import { formatCount } from "./orgChartModel";

// Floating list of positions with no parent. It owns its search state, so
// typing here never re-renders the chart, and it only mounts the cards in view.
const ROW_H = 70;
const FLASH_MS = 1800;

function UnassignedPanel({
  open,
  jobs,
  hint,
  cardTitle,
  assignTitle,
  draggingId,
  focusRequest,
  avatarGradient,
  initials,
  onClose,
  onDragStart,
  onDragEnd,
  onCardClick,
  onCardDoubleClick,
  onAssign,
}) {
  const [query, setQuery] = useState("");
  const [flashId, setFlashId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((j) =>
      (j.name || "").toLowerCase().includes(q) ||
      (j.code || "").toLowerCase().includes(q) ||
      (j.functionName || "").toLowerCase().includes(q),
    );
  }, [jobs, query]);

  // A search result elsewhere on the page asked to show one of these jobs.
  useEffect(() => {
    if (!focusRequest) return undefined;
    setQuery("");
    setFlashId(focusRequest.id);
    const t = setTimeout(() => setFlashId(null), FLASH_MS);
    return () => clearTimeout(t);
  }, [focusRequest]);

  const focusIndex = useMemo(
    () => (flashId ? filtered.findIndex((j) => j.id === flashId) : -1),
    [filtered, flashId],
  );

  return (
    <div className={`joh-unassigned-panel${!open ? " collapsed" : ""}`} aria-hidden={!open}>
      <div className="joh-unassigned-panel-header">
        <div style={{ textAlign: "left" }}>
          <h3>UNASSIGNED POSITIONS</h3>
          <span>(No Parent) · {formatCount(jobs.length)}</span>
        </div>
        <button className="joh-unassigned-close-btn" onClick={onClose} title="Hide Panel">
          ✕
        </button>
      </div>

      <div className="roles-search joh-unassigned-search">
        <input
          type="text"
          placeholder="Search unassigned positions..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="search-input"
        />
        <button
          className="secondary-btn sm"
          onClick={() => setQuery("")}
          title="Clear search"
          disabled={!query}
        >
          Clear
        </button>
      </div>

      <div className="joh-unassigned-drag-hint">{hint}</div>

      {filtered.length === 0 ? (
        <div className="joh-unassigned-panel-body">
          <div className="joh-unassigned-empty">
            {query ? "No matching jobs found" : "All positions are assigned!"}
          </div>
        </div>
      ) : (
        <VirtualList
          className="joh-unassigned-panel-body is-virtual"
          items={filtered}
          itemHeight={ROW_H}
          padding={12}
          resetKey={query}
          scrollToIndex={focusIndex >= 0 ? focusIndex : null}
          renderItem={(job, i, style) => {
            const [g1, g2] = avatarGradient(job.name);
            const cls = [
              "joh-unassigned-card",
              draggingId === job.id ? "dragging" : "",
              flashId === job.id ? "flash" : "",
            ].filter(Boolean).join(" ");
            return (
              <div key={job.id} style={{ ...style, paddingBottom: 8 }}>
                <div
                  className={cls}
                  draggable
                  onDragStart={(e) => onDragStart(e, job)}
                  onDragEnd={onDragEnd}
                  onClick={onCardClick ? () => onCardClick(job) : undefined}
                  onDoubleClick={() => onCardDoubleClick(job)}
                  title={cardTitle}
                >
                  <div
                    className="joh-unassigned-card-avatar"
                    style={{ background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
                  >
                    {initials(job.name)}
                  </div>
                  <div className="joh-unassigned-card-content">
                    <div className="joh-unassigned-card-title" title={job.name}>{job.name}</div>
                    <div className="joh-unassigned-card-meta">
                      {job.code && <span className="joh-unassigned-card-code">{job.code}</span>}
                      {job.functionName && (
                        <span className="joh-unassigned-card-dept" title={job.functionName}>{job.functionName}</span>
                      )}
                    </div>
                  </div>
                  <button
                    className="joh-unassigned-assign-btn"
                    title={assignTitle}
                    onClick={(e) => { e.stopPropagation(); onAssign(job); }}
                  >
                    🔗
                  </button>
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
}

export default memo(UnassignedPanel);
